import type { DocumentDto, Paged } from '@ukinfo/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ApiError, api } from '../lib/api';
import { Pagination } from '../components/Pagination';
import { ScopedEmptyState } from '../components/ScopedEmptyState';
import { useAuth } from '../lib/auth';
import { scopeQuery, useScope } from '../lib/scope';
import { DOCUMENT_CATEGORY_LABELS, formatDate } from '../lib/format';
import { OrgUnitPicker } from '../components/OrgUnitPicker';
import { PageHeader } from '../components/PageHeader';
import {
  Badge,
  Button,
  Card,
  CardBody,
  Dialog,
  ErrorBlock,
  Field,
  Input,
  LoadingBlock,
  Select,
  Textarea,
  cn,
  useToast,
} from '../components/ui';

const VISIBILITY_LABELS: Record<string, string> = {
  UNIT: 'Nur im Bereich',
  CLUB: 'Ganzer Verein',
  PUBLIC: 'Öffentlich',
};

export function KnowledgePage(): JSX.Element {
  const scope = useScope();
  const { can } = useAuth();
  // /wissen/<id> (aus der Suche): Dokument hervorheben.
  const { id: docId } = useParams();
  const [category, setCategory] = useState('');
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);

  const query = {
    ...scopeQuery(scope),
    ...(category ? { category } : {}),
    ...(search.trim().length >= 2 ? { search: search.trim() } : {}),
  };

  const [page, setPage] = useState(1);
  useEffect(() => setPage(1), [JSON.stringify(query)]);

  const seite = useQuery({
    queryKey: ['documents', query, page],
    queryFn: () => api.get<Paged<DocumentDto>>('/api/documents', { ...query, page }),
  });
  const { isLoading, error } = seite;
  const data = seite.data?.items;

  // Einzeln laden: das Dokument liegt evtl. nicht auf der aktuellen Seite oder im gewählten Bereich.
  const einzeln = useQuery({
    queryKey: ['documents', 'detail', docId],
    queryFn: () => api.get<DocumentDto>(`/api/documents/${docId}`),
    enabled: Boolean(docId),
    retry: false,
  });
  const inListe = Boolean(docId && data?.some((doc) => doc.id === docId));

  return (
    <div className="space-y-4">
      <PageHeader
        title="Wissen"
        description="Dokumente, Protokolle, Formulare und Checklisten"
        actions={
          can('documents', 'create') && (
            <Button onClick={() => setDialogOpen(true)}>+ Dokument</Button>
          )
        }
      />

      <Card>
        <CardBody className="flex flex-wrap gap-2">
          <Select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="w-auto min-w-40"
          >
            <option value="">Alle Kategorien</option>
            {Object.entries(DOCUMENT_CATEGORY_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
          <Input
            placeholder="Suchen …"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-auto min-w-40 flex-1"
          />
        </CardBody>
      </Card>

      {docId && einzeln.error && <ErrorBlock error={einzeln.error} />}
      {docId && einzeln.data && !inListe && (
        <ul className="grid gap-2 sm:grid-cols-2">
          <DocumentCard doc={einzeln.data} highlighted />
        </ul>
      )}

      {isLoading && <LoadingBlock />}
      {error && <ErrorBlock error={error} />}
      {data && data.length === 0 && (
        <ScopedEmptyState
          title="Keine Dokumente gefunden"
          description="Für die aktuelle Auswahl ist nichts hinterlegt."
          filtersActive={category !== '' || search !== ''}
          onResetFilters={() => {
            setCategory('');
            setSearch('');
          }}
          createAction={
            can('documents', 'create') ? (
              <Button onClick={() => setDialogOpen(true)}>+ Dokument</Button>
            ) : undefined
          }
        />
      )}

      <ul className="grid gap-2 sm:grid-cols-2">
        {(data ?? []).map((doc) => (
          <DocumentCard key={doc.id} doc={doc} highlighted={doc.id === docId} />
        ))}
      </ul>

      {seite.data && (
        <Pagination
          page={seite.data.page}
          pageSize={seite.data.pageSize}
          total={seite.data.total}
          onPageChange={setPage}
        />
      )}

      <DocumentDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />
    </div>
  );
}

function DocumentCard({ doc, highlighted = false }: { doc: DocumentDto; highlighted?: boolean }): JSX.Element {
  return (
    <li ref={highlighted ? (el) => el?.scrollIntoView({ block: 'center' }) : undefined}>
        <Card className={cn('h-full', highlighted && 'ring-2 ring-brand-500')}>
          <CardBody className="flex h-full flex-col gap-2">
            <div className="flex items-start justify-between gap-2">
              <p className="min-w-0 flex-1 font-medium text-slate-900">{doc.title}</p>
              <Badge tone="info">
                {DOCUMENT_CATEGORY_LABELS[doc.category] ?? doc.category}
              </Badge>
            </div>
            {doc.description && <p className="text-sm text-slate-600">{doc.description}</p>}
            <div className="flex flex-wrap gap-1">
              {doc.tags.map((tag) => (
                <Badge key={tag}>{tag}</Badge>
              ))}
            </div>
            <p className="text-xs text-slate-500">
              {doc.organisationUnits.map((u) => u.name).join(', ')} ·{' '}
              {VISIBILITY_LABELS[doc.visibility] ?? doc.visibility} · Version {doc.version}
            </p>
            <p className="text-xs text-slate-400">
              {doc.createdByName ? `${doc.createdByName}, ` : ''}
              {formatDate(doc.updatedAt)}
            </p>
            <div className="mt-auto pt-2">
              {doc.downloadUrl ? (
                <a
                  href={doc.downloadUrl}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="text-sm font-medium text-brand-700 hover:underline"
                >
                  {doc.storageProvider === 'LOCAL' ? 'Herunterladen' : 'Öffnen'}
                </a>
              ) : (
                <span className="text-sm text-slate-400">Keine Datei hinterlegt</span>
              )}
            </div>
          </CardBody>
        </Card>
    </li>
  );
}

function DocumentDialog({ open, onClose }: { open: boolean; onClose: () => void }): JSX.Element {
  const scope = useScope();
  const queryClient = useQueryClient();
  const toast = useToast();
  const fileRef = useRef<HTMLInputElement>(null);

  const [mode, setMode] = useState<'link' | 'file'>('link');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('SONSTIGES');
  const [visibility, setVisibility] = useState('UNIT');
  const [externalUrl, setExternalUrl] = useState('');
  const [tags, setTags] = useState('');
  const [unitIds, setUnitIds] = useState<string[]>(scope.unitId ? [scope.unitId] : []);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const reset = (): void => {
    setTitle('');
    setDescription('');
    setExternalUrl('');
    setTags('');
    setErrors({});
    if (fileRef.current) fileRef.current.value = '';
  };

  const save = useMutation({
    mutationFn: async () => {
      if (mode === 'file') {
        const file = fileRef.current?.files?.[0];
        if (!file) throw new ApiError(422, null, 'Bitte eine Datei auswählen');
        const form = new FormData();
        form.append('title', title || file.name);
        form.append('description', description);
        form.append('category', category);
        form.append('visibility', visibility);
        form.append('organisationUnitIds', unitIds.join(','));
        form.append('tags', tags);
        form.append('file', file);
        return api.upload<DocumentDto>('/api/documents/upload', form);
      }
      return api.post<DocumentDto>('/api/documents', {
        title,
        description: description || null,
        category,
        storageProvider: 'EXTERNAL_LINK',
        externalUrl,
        visibility,
        organisationUnitIds: unitIds,
        tags: tags
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean),
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['documents'] });
      toast.show('Dokument gespeichert');
      reset();
      onClose();
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError) {
        setErrors(err.fieldErrors());
        toast.show(err.message, 'error');
      } else {
        toast.show('Speichern fehlgeschlagen', 'error');
      }
    },
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Dokument hinzufügen"
      wide
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Abbrechen
          </Button>
          <Button
            onClick={() => save.mutate()}
            loading={save.isPending}
            disabled={unitIds.length === 0}
          >
            Speichern
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="flex gap-2">
          <Button
            variant={mode === 'link' ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => setMode('link')}
          >
            Externer Link
          </Button>
          <Button
            variant={mode === 'file' ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => setMode('file')}
          >
            Datei hochladen
          </Button>
        </div>
        <p className="text-xs text-slate-500">
          Große Fotos und Videos gehören in den bestehenden Cloud-Speicher und werden hier nur
          verlinkt. Kleine Dateien können direkt hochgeladen werden.
        </p>

        <Field label="Titel" required error={errors['title']}>
          {(id) => <Input id={id} value={title} onChange={(e) => setTitle(e.target.value)} />}
        </Field>

        {mode === 'link' ? (
          <Field label="Link" required error={errors['externalUrl']}>
            {(id) => (
              <Input
                id={id}
                value={externalUrl}
                onChange={(e) => setExternalUrl(e.target.value)}
                placeholder="https://drive.google.com/…"
              />
            )}
          </Field>
        ) : (
          <Field label="Datei" required>
            {(id) => <input id={id} ref={fileRef} type="file" className="input py-1.5" />}
          </Field>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Kategorie">
            {(id) => (
              <Select id={id} value={category} onChange={(e) => setCategory(e.target.value)}>
                {Object.entries(DOCUMENT_CATEGORY_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="Sichtbarkeit">
            {(id) => (
              <Select id={id} value={visibility} onChange={(e) => setVisibility(e.target.value)}>
                {Object.entries(VISIBILITY_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        </div>

        <Field label="Bereiche" required error={errors['organisationUnitIds']}>
          {() => <OrgUnitPicker value={unitIds} onChange={setUnitIds} />}
        </Field>

        <Field label="Schlagwörter" hint="Durch Komma getrennt">
          {(id) => <Input id={id} value={tags} onChange={(e) => setTags(e.target.value)} />}
        </Field>

        <Field label="Beschreibung">
          {(id) => (
            <Textarea
              id={id}
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          )}
        </Field>
      </div>
    </Dialog>
  );
}
